import React, { useEffect } from 'react'

const LATEST_DRAFT_SELECTOR = '[data-latest-draft="true"]'
const POPOVER_SELECTOR = '.ant-popover.ai-draft-popover-overlay'
const HEADER_SELECTORS = ['.navbar-header', '.order-title-container']

/** Estimated popover height for fallback calculation when popover isn't positioned yet */
const POPOVER_ESTIMATED_HEIGHT = 180

/** Maximum distance (px) between popover and anchor to be considered valid positioning */
const MAX_POPOVER_ANCHOR_DISTANCE = 500

const SCROLL_TOP_PADDING = 16

/** Delay to allow Ant Design popover to complete positioning before measuring */
export const SCROLL_DELAY_MS = 250

/**
 * Auto-scrolls to the AI draft popover when a pending draft exists.
 *
 * Handles Ant Design popover positioning race conditions by:
 * 1. Waiting for popover to render and position (250ms delay)
 * 2. Validating popover is near anchor element (prevents false positioning at 0,0)
 * 3. Falling back to anchor-based calculation if popover position is invalid
 *
 * Accounts for fixed headers to ensure popover is fully visible.
 */
const AutoScrollToAiDraft: React.FC = () => {
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }

    // Force immediate scroll to top to override browser scroll restoration
    window.scrollTo(0, 0)

    const performAutoScroll = () => {
      const anchorElement = document.querySelector<HTMLElement>(LATEST_DRAFT_SELECTOR)
      if (!anchorElement) {
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'auto'
        }
        return
      }

      const totalHeaderHeight = HEADER_SELECTORS.reduce((total, selector) => {
        const element = document.querySelector<HTMLElement>(selector)
        return total + (element?.offsetHeight || 0)
      }, 0)

      const anchorRect = anchorElement.getBoundingClientRect()
      const absoluteAnchorTop = anchorRect.top + window.pageYOffset

      const popoverElement = document.querySelector<HTMLElement>(POPOVER_SELECTOR)
      let targetScrollY = 0

      if (popoverElement) {
        const popoverRect = popoverElement.getBoundingClientRect()
        const absolutePopoverTop = popoverRect.top + window.pageYOffset
        const distance = Math.abs(absolutePopoverTop - absoluteAnchorTop)

        const isPopoverPositionValid = distance < MAX_POPOVER_ANCHOR_DISTANCE && absolutePopoverTop > 0

        if (isPopoverPositionValid) {
          targetScrollY = absolutePopoverTop - totalHeaderHeight - SCROLL_TOP_PADDING
        } else {
          targetScrollY = absoluteAnchorTop - totalHeaderHeight - POPOVER_ESTIMATED_HEIGHT
        }
      } else {
        targetScrollY = absoluteAnchorTop - totalHeaderHeight - POPOVER_ESTIMATED_HEIGHT
      }

      window.scrollTo({
        top: Math.max(0, targetScrollY),
        behavior: 'smooth'
      })

      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto'
      }
    }

    const timer = setTimeout(performAutoScroll, SCROLL_DELAY_MS)
    return () => {
      clearTimeout(timer)
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto'
      }
    }
  }, [])

  return null
}

export default AutoScrollToAiDraft
